import { useEffect, useRef, useCallback } from "react";
import type { SponsorTier } from "@wondermaps/db";
import { analytics, setAnalyticsContext } from "../lib/analytics";

/**
 * Hook to set analytics context from route params.
 * Call this at the top level of visitor map routes.
 */
export function useAnalyticsContext(ctx: {
  org_slug?: string;
  map_slug?: string;
}) {
  useEffect(() => {
    setAnalyticsContext(ctx);
  }, [ctx.org_slug, ctx.map_slug]);
}

/**
 * Hook for tracking POI detail sheet engagement.
 * Fires engage_poi_content after 5 seconds of viewing.
 */
export function useEngagementTimer(
  poi: { id: string; name: string; sponsorTier?: SponsorTier | null } | null,
  isOpen: boolean
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (isOpen && poi) {
      startTimeRef.current = Date.now();
      firedRef.current = false;

      timerRef.current = setTimeout(() => {
        if (!firedRef.current) {
          analytics.engagePoiContent({
            poi_id: poi.id,
            poi_name: poi.name,
            duration_seconds: 5,
            is_sponsored: !!poi.sponsorTier,
            sponsor_tier: poi.sponsorTier ?? null,
          });
          firedRef.current = true;
        }
      }, 5000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOpen, poi?.id, poi?.name, poi?.sponsorTier]);
}

/**
 * Hook for debounced search tracking.
 * Fires search_performed after 1.5s of no typing (min 2 chars).
 */
export function useSearchTracking() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef<string>("");

  const trackSearch = useCallback((query: string, resultCount: number) => {
    // Clear previous timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Only track if 2+ chars
    if (query.length < 2) {
      lastQueryRef.current = "";
      return;
    }

    // Debounce 1.5s
    timerRef.current = setTimeout(() => {
      // Avoid duplicate tracking for same query
      if (query !== lastQueryRef.current) {
        analytics.searchPerformed({ query, result_count: resultCount });
        lastQueryRef.current = query;
      }
    }, 1500);
  }, []);

  const trackSearchConversion = useCallback(
    (query: string, poi: { id: string; name: string }) => {
      if (query.length >= 2) {
        analytics.searchConverted({
          query,
          poi_id: poi.id,
          poi_name: poi.name,
        });
      }
    },
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return { trackSearch, trackSearchConversion };
}

// Re-export analytics for convenience
export { analytics };
